import { editorSaved } from 'src/app/shared/utils/editor-close.utils';
import {
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { selectUser } from '../../reducers';

import { Alert } from '../../models/alert.models';
import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { UserFacadeService } from '@services/user-facade.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  TriggeredAlertsScope,
  injectDeleteAlertMutation,
  injectTriggeredAlertsQuery,
} from 'src/app/api/queries/alert.queries';
import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';
import { DialogService } from '@sui/dialog';
import { ConfirmDialogComponent } from '../../components/popups/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-alerts-center',
  templateUrl: './alerts-center.component.html',
  styleUrl: './alerts-center.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertsCenterComponent {
  readonly searchQuery = signal<string>('');

  readonly AppFeatures = AppFeatures;

  private dialogService = inject(DialogService);
  private readonly store = inject(Store<AppState>);
  private readonly deleteAlertMutation = injectDeleteAlertMutation();

  private readonly user = this.store.selectSignal(selectUser);

  private readonly scope = computed<TriggeredAlertsScope | null>(() => {
    const user = this.user();

    return user?.id
      ? { userId: user.id, companyId: user.active_company_id }
      : null;
  });

  private readonly alertsQuery = injectTriggeredAlertsQuery(this.scope);

  readonly alertsLoading = this.alertsQuery.isLoading;

  readonly alerts = computed<Alert[]>(() => {
    const alerts = this.alertsQuery.data() ?? [];
    const query = this.searchQuery().toLowerCase();

    return query
      ? alerts.filter(
          (alert) =>
            alert.title.toLowerCase().includes(query) ||
            alert.feed_title.toLowerCase().includes(query),
        )
      : alerts;
  });

  constructor(
    public userFacade: UserFacadeService,
    private editService: ObjectEditorService,
    private translateService: TranslateService,
  ) {}

  handleSearchStringAlertChange(query: string) {
    this.searchQuery.set(query ?? '');
  }

  editAlert(alert) {
    // * the list is an Angular Query; a saved edit re-fetches it
    editorSaved(this.editService.editAlert(alert?.id)).then((saved) => {
      if (saved) {
        void this.alertsQuery.refetch();
      }
    });
  }

  deleteAlert(alert) {
    const title = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation-title',
    );

    const message = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          // No `deleteAlertSuccess` broadcast here, deliberately: it decrements
          // the current feed's alert badge, which this alert need not belong to.
          this.deleteAlertMutation.mutate(alert.id);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  trackByAlertId(index: number, alert: Alert): string {
    return alert.id;
  }
}
